import React, { FC, Fragment, useContext, useEffect, useState } from 'react';

import { FeedArea } from '@go-shared/types/feedTemplate';
import { findEventsBySailingDays } from '@go-client/lib/api/eventTemplate';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { Language } from '@go-shared/types/info';
import { Event } from '@go-shared/types/event';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { generateTimeString } from '@go-shared/lib/event';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { getVenueLocation } from '@go-shared/lib/venue';
import { getLocaleUTCDateString, timeToMinutes } from '@go-shared/lib/time';
import { filterRundownEvents, groupRundownEvents } from '@go-shared/lib/feed';
import { ContentTemplateParams } from '@go-shared/types/contentTemplate';
import { evaluateMergedVars } from '@go-shared/lib/mergedVars';
import { makeSailingDayBundle } from '@go-shared/lib/sailing';
import { populate as populateSailing, populateDay } from '@go-shared/lib/itinerary';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { safeIdCompare } from '@go-shared/lib/utils';
import { BrandCommonTextValues } from '@go-shared/types/brand';
import { getCommonTexts } from '@go-shared/lib/brand';

interface MultiDayProps {
  selectedLanguage: Language | undefined;
  area: FeedArea;
  showMergedVars: boolean;
}

export const MultiDay: FC<MultiDayProps> = ({ selectedLanguage, area, showMergedVars }) => {
  const { rundownFontSize, sailing, sailingDay, commonTextTranslations } =
    useContext(FeedEditorCtx);
  const { languages, defaultLanguage, brand } = useServerInfo();
  const { activeProperty } = useActiveProperty();
  const [perDayEvents, setPerDayEvents] = useState<[string, Event[]][]>([]);

  const commonTexts = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: commonTextTranslations,
    },
    { selected: selectedLanguage?.key ?? defaultLanguage.key }
  );

  useEffect(() => {
    if (!area.params?.multiDayConfig?.startDate || !area.params?.multiDayConfig?.endDate) {
      return;
    }

    getEventsFromSailingDays();
  }, [
    selectedLanguage,
    area.params?.multiDayConfig?.startDate,
    area.params?.multiDayConfig?.endDate,
  ]);

  async function getEventsFromSailingDays() {
    const perDayEvents = await findEventsBySailingDays({
      propertyId: activeProperty._id,
      lang: selectedLanguage?.key,
      startDate: area.params?.multiDayConfig?.startDate!,
      endDate: area.params?.multiDayConfig?.endDate!,
    });

    setPerDayEvents(perDayEvents);
  }

  const globalSailingBundle =
    sailing && sailingDay
      ? makeSailingDayBundle(
          populateSailing(sailing, activeProperty, [], []),
          populateDay(sailingDay, [])
        )
      : undefined;

  const eventSorter = makeRundownEventSorter(360);
  const hourFormat = activeProperty.settings?.hourFormat ?? activeProperty.brand.hourFormat;
  const deckPrefix = getFilteredRundownDeckPrefix(commonTexts.deck, area.params);
  const lineHeight = fontSizeToLineHeight(rundownFontSize);

  return (
    <div
      style={{ lineHeight, fontSize: 10 }}
      className="feed-multi-day relative w-full"
      key={area._id}
    >
      <div
        className="relative w-full flex flex-row flex-wrap"
        style={{
          top: 0,
          left: 0,
          maxHeight: '100%',
        }}
      >
        {area.params?.eventRundownTitle && (
          <h1 className="feed-multi-day-title">{area.params.eventRundownTitle}</h1>
        )}
        <div className="feed-multi-day-body relative w-full flex flex-row flex-wrap">
          <div
            className="feed-multi-day-header flex border-b border-grey-dark py-1 font-bold"
            style={{
              breakInside: 'avoid',
              width: '100%',
            }}
          >
            <div className="px-1" style={{ width: '20%' }}>
              TIME
            </div>
            <div className="px-1" style={{ width: '50%' }}>
              ACTIVITY
            </div>
            <div className="px-1" style={{ width: '30%' }}>
              LOCATION
            </div>
          </div>

          {perDayEvents.map(([date, events], i) => {
            const dayTitle = getLocaleUTCDateString(new Date(date), 'en-EN', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            });
            const filteredEvents = area.params?.eventRundownFilter
              ? filterRundownEvents(events, area.params.eventRundownFilter)
              : events;
            const groupedEvents = groupRundownEvents(
              filteredEvents,
              area.params?.eventRundownTimeGrouping
            );

            filteredEvents.sort(eventSorter);

            return (
              <div className="feed-multi-day-day" key={i}>
                <h1 className="feed-multi-day-day-title">{dayTitle}</h1>
                {groupedEvents.map((eventGroup, i) => (
                  <Fragment key={i}>
                    {eventGroup.groupName && (
                      <h2 className="feed-multi-day-group-title">{eventGroup.groupName}</h2>
                    )}
                    <div
                      className="feed-multi-day-group-header flex border-b border-grey-dark py-1 font-bold"
                      style={{
                        breakInside: 'avoid',
                        width: '100%',
                      }}
                    >
                      <div className="px-1" style={{ width: '20%' }}>
                        TIME
                      </div>
                      <div className="px-1" style={{ width: '50%' }}>
                        ACTIVITY
                      </div>
                      <div className="px-1" style={{ width: '30%' }}>
                        LOCATION
                      </div>
                    </div>

                    {eventGroup.events.map((event, i) => {
                      let timeString = generateTimeString(
                        event.startTime,
                        event.duration,
                        hourFormat,
                        commonTexts,
                        event.interactiveFeedDisplay,
                        event.template.interactiveFeedDisplay,
                        undefined,
                        area.params
                      );

                      const [titleString] = evaluateMergedVars(
                        event.template.title,
                        globalSailingBundle,
                        languages.map(l => l.key),
                        selectedLanguage?.key,
                        defaultLanguage.key,
                        event.mergedVars
                      );

                      const eventHighlights =
                        activeProperty.brand.eventHighlights.filter(
                          a =>
                            (event.highlights || []).findIndex(b => safeIdCompare(a._id, b._id)) >
                            -1
                        ) || [];

                      const attributes =
                        activeProperty.brand.attributes.filter(
                          a =>
                            (event.highlights || []).findIndex(b => safeIdCompare(a._id, b._id)) >
                            -1
                        ) || [];

                      return (
                        <div
                          className="feed-multi-day-group-row border-b border-grey-dark py-1"
                          key={i}
                        >
                          <div
                            className="flex"
                            key={event._id}
                            style={{
                              breakInside: 'avoid',
                              width: '100%',
                            }}
                          >
                            <div className="px-1" style={{ width: '20%' }}>
                              {timeString}
                            </div>
                            <div className="px-1" style={{ width: '50%' }}>
                              {showMergedVars ? titleString : event.template.title}
                            </div>
                            <div className="px-1" style={{ width: '30%' }}>
                              <span>{event.venueDisplayName || event.venue.name}</span>
                              <br />
                              {event.deckDisplayName ? (
                                <span>{event.deckDisplayName}</span>
                              ) : (
                                deckPrefix && (
                                  <>
                                    <span>
                                      {deckPrefix} {event.venue.level}
                                    </span>
                                    {getVenueLocationElement(event.venue, commonTexts)}
                                  </>
                                )
                              )}
                            </div>
                            <div className="hidden multi-day-attributes">
                              {attributes.map(a => {
                                let imgDiv = <></>;
                                let nameDiv = (
                                  <span className={`multi-day-attribute_${a.name}`}>
                                    <span>{a.name}</span>
                                  </span>
                                );
                                if (a.iconUrl) {
                                  imgDiv = <img src={a.iconUrl} />;
                                }
                                return (
                                  <Fragment key={a._id}>
                                    {imgDiv}
                                    {nameDiv}
                                  </Fragment>
                                );
                              })}

                              {eventHighlights.map(a => {
                                let nameDiv = (
                                  <span className={`rundown-highlight_${a.name}`}>
                                    <span>{a.name}</span>
                                  </span>
                                );

                                return <Fragment key={a._id}>{nameDiv}</Fragment>;
                              })}
                            </div>
                          </div>
                          {area.params?.eventRundownShowDescription && (
                            <div className="px-1" style={{ width: '100%' }}>
                              {event.template.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function fontSizeToLineHeight(fontSize: number) {
  let increment = (fontSize - 16) * 0.125;

  return 1.5 + increment;
}

function getVenueLocationElement(venue: VenueTemplate, commonTexts: BrandCommonTextValues) {
  let location = getVenueLocation(venue, commonTexts);
  return location !== '' ? <span> {location}</span> : null;
}

function makeRundownEventSorter(dayStart: number) {
  return (e1: Event, e2: Event) => {
    let startTime1 = timeToMinutes(e1.startTime, dayStart);
    let startTime2 = timeToMinutes(e2.startTime, dayStart);

    let duration1 = e1.duration;
    let duration2 = e2.duration;

    // sort by start time
    if (startTime1 !== startTime2) return startTime1 - startTime2;

    // if they are the same, sort by end time
    if (duration1 !== duration2) return duration1 - duration2;

    // otherwise, sort by title
    return e1.template.title.localeCompare(e2.template.title);
  };
}

// FIXME: This is copied from server/lib/feed.ts
function getFilteredRundownDeckPrefix(defaultDeckValue: string, params?: ContentTemplateParams) {
  // If params and eventRundownDeckPrefix are defined...
  if (params && params.eventRundownDeckPrefix !== undefined) {
    // if the prefix is the empty string, return undefined
    if (params.eventRundownDeckPrefix === '') return undefined;
    // Otherwise return the value
    return params.eventRundownDeckPrefix;
  }
  // If params or eventRundownDeckPrefix are undefined, return the default ('Deck')
  return defaultDeckValue;
}
